<template>
  <div 
    class="notification-toast" 
    :class="[type, { visible: isVisible }]"
    role="alert"
    aria-live="assertive"
  >
    <div class="toast-icon">
      <span class="material-icons">{{ icon }}</span>
    </div>
    <div class="toast-content">
      <h4>{{ title }}</h4>
      <p>{{ message }}</p>
    </div>
    <button 
      class="toast-close" 
      @click="hide"
      aria-label="Fermer la notification"
    >
      <span class="material-icons">close</span>
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'

const props = defineProps({
  message: {
    type: String,
    default: ''
  },
  title: {
    type: String,
    default: 'Notification'
  },
  type: {
    type: String,
    default: 'info', // 'success', 'warning', 'error', 'info'
    validator: value => ['success', 'warning', 'error', 'info'].includes(value)
  },
  duration: {
    type: Number,
    default: 3000 // ms
  },
  autoClose: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['close'])
const isVisible = ref(false)
let timeoutId = null

const icon = computed(() => {
  const icons = {
    success: 'check_circle',
    warning: 'warning',
    error: 'error',
    info: 'info'
  }
  return icons[props.type] || 'info'
})

function show() {
  isVisible.value = true
  
  if (props.autoClose) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => {
      hide()
    }, props.duration)
  }
}

function hide() {
  isVisible.value = false
  clearTimeout(timeoutId)
  emit('close')
}

// Montrer la notification au montage
onMounted(() => {
  show()
})

// Nettoyer le timeout à la destruction
onBeforeUnmount(() => {
  clearTimeout(timeoutId)
})

// Exposer les méthodes au parent
defineExpose({
  show,
  hide
})

// Réinitialiser le timer si le message change
watch(
  () => props.message,
  () => {
    if (isVisible.value && props.autoClose) {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        hide()
      }, props.duration)
    }
  }
)
</script>

<style scoped>
.notification-toast {
  position: fixed;
  bottom: -100px;
  right: 20px;
  min-width: 300px;
  max-width: 450px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: flex-start;
  padding: 12px 16px;
  z-index: 10000;
  transition: all 0.3s ease-in-out;
  opacity: 0;
}

.notification-toast.visible {
  bottom: 20px;
  opacity: 1;
}

.toast-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}

.toast-icon .material-icons {
  font-size: 24px;
}

.toast-content {
  flex: 1;
}

.toast-content h4 {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
}

.toast-content p {
  margin: 0;
  font-size: 14px;
  color: #555;
}

.toast-close {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
}

.toast-close:hover {
  color: #333;
}

.toast-close .material-icons {
  font-size: 18px;
}

/* Types de notifications */
.notification-toast.success {
  border-left: 4px solid #4caf50;
}

.notification-toast.success .toast-icon {
  color: #4caf50;
}

.notification-toast.warning {
  border-left: 4px solid #ff9800;
}

.notification-toast.warning .toast-icon {
  color: #ff9800;
}

.notification-toast.error {
  border-left: 4px solid #f44336;
}

.notification-toast.error .toast-icon {
  color: #f44336;
}

.notification-toast.info {
  border-left: 4px solid #2196f3;
}

.notification-toast.info .toast-icon {
  color: #2196f3;
}
</style>
